import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import { compactActionSx, surfaceSx } from "./ownerCenterUi";

const ARGENTINA_TZ = "America/Argentina/Mendoza";
const ZOOM_STEPS = [60, 30, 15];

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function argentinaParts(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ARGENTINA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  return {
    ymd: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: (Number(parts.hour) * 60) + Number(parts.minute),
    clock: `${parts.hour}:${parts.minute}`,
  };
}

function ymd(value = new Date()) {
  return argentinaParts(value)?.ymd || "";
}

function clockMinutes(value) {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/);
  return match ? (Number(match[1]) * 60) + Number(match[2]) : 0;
}

function localMinutes(value) {
  return argentinaParts(value)?.minutes ?? null;
}

function localDateTimeInput(dateKey, minutes) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${dateKey}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function minuteLabel(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function durationLabel(minutes) {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function plannedBlocks(calendar, now = new Date()) {
  const day = calendar?.day || {};
  const blocks = [];
  (day.routines || []).forEach((routine) => {
    const start = clockMinutes(routine.startTime);
    const end = clockMinutes(routine.endTime);
    if (end <= start) return;
    blocks.push({
      id: `routine:${idOf(routine)}`,
      type: "routine",
      title: routine.name,
      start,
      end,
      completed: Boolean(routine.completed),
      source: routine,
    });
  });
  (day.commitments || []).forEach((item) => {
    const notes = String(item?.notes || "");
    if (notes.includes("[work-log]")) return;
    const start = localMinutes(item.scheduledStartAt);
    if (start === null) return;

    const isLive = notes.includes("[day-live-block]") && item.status === "open" && String(item?.work?.state || "") === "in_progress";
    if (notes.includes("[day-live-block]") && !isLive) return;

    const nowParts = argentinaParts(now);
    const selected = day?.date || day?.selectedDate || calendar?.selectedDate || "";
    const liveEnd = nowParts?.ymd === selected ? Math.max(start + 1, nowParts.minutes) : 1440;
    const duration = Math.max(15, Number(item.estimatedMinutes || 45));
    blocks.push({
      id: `commitment:${idOf(item)}`,
      type: isLive ? "live" : "commitment",
      title: item.title,
      start,
      end: Math.min(1440, isLive ? liveEnd : start + duration),
      completed: item.status === "done",
      actualMinutes: Number(item.actualMinutes || 0),
      source: item,
    });
  });
  return blocks;
}

function actualBlocks(calendar) {
  return (calendar?.day?.timeEntries || []).map((entry) => {
    const start = localMinutes(entry.occurredAt);
    if (start === null) return null;
    const minutes = Math.max(1, Number(entry.minutes || 0));
    return {
      id: `actual:${idOf(entry)}`,
      type: "actual",
      title: entry.commitment?.title || entry.note || "Actividad registrada",
      start,
      end: Math.min(1440, start + minutes),
      minutes,
      note: entry.note,
      source: entry,
    };
  }).filter(Boolean);
}

function dayNotes(entries = [], selectedDate) {
  return entries.map((entry) => {
    const parts = argentinaParts(entry?.occurredAt || entry?.createdAt);
    if (!parts || parts.ymd !== selectedDate) return null;
    return {
      id: `note:${idOf(entry)}`,
      type: entry?.kind === "event" || entry?.structuredData?.dayMoment ? "moment" : "note",
      title: entry.text,
      start: parts.minutes,
      end: parts.minutes + 1,
      status: entry.status || "inbox",
      source: entry,
    };
  }).filter(Boolean);
}

function bucketOf(start, zoomMinutes) {
  return Math.floor(Math.max(0, Math.min(1439, start)) / zoomMinutes) * zoomMinutes;
}

function ContinuationCard({ block }) {
  const live = block.type === "live";
  return (
    <Box sx={{ px: .9, py: .45, borderRadius: 1.35, bgcolor: live ? "rgba(243,90,58,.045)" : "action.hover", borderLeft: "3px solid", borderColor: live ? "primary.main" : "divider" }}>
      <Stack direction="row" spacing={.6} alignItems="center" justifyContent="space-between">
        <Typography noWrap sx={{ minWidth: 0, fontWeight: 850, fontSize: 10.7, color: live ? "primary.main" : "text.secondary" }}>{block.title}</Typography>
        <Typography sx={{ flexShrink: 0, fontWeight: 850, fontSize: 9.4, color: "text.secondary" }}>{live ? "en curso" : "continúa"}</Typography>
      </Stack>
    </Box>
  );
}

function EventCard({ block, onLogTime, onRoutineToggle, onProcessNote }) {
  if (block.type === "live") {
    return (
      <Box sx={{ px: 1, py: .9, borderRadius: 1.7, bgcolor: "rgba(243,90,58,.06)", border: "1px solid", borderColor: "primary.main" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={.55} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 1000, fontSize: 12.8, lineHeight: 1.3 }}>{block.title}</Typography>
              <Chip size="small" color="primary" label="EN CURSO" sx={{ height: 20, fontWeight: 1000, fontSize: 9.2 }} />
            </Stack>
            <Typography sx={{ mt: .2, color: "text.secondary", fontWeight: 780, fontSize: 10.9 }}>
              desde {minuteLabel(block.start)} · {durationLabel(Math.max(1, block.end - block.start))}
            </Typography>
          </Box>
          <TimerRoundedIcon color="primary" sx={{ fontSize: 18, flexShrink: 0 }} />
        </Stack>
      </Box>
    );
  }

  if (block.type === "actual") {
    return (
      <Box sx={{ px: 1, py: .8, borderRadius: 1.7, bgcolor: "action.hover", borderLeft: "3px solid", borderColor: "primary.main" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, fontSize: 12.5, lineHeight: 1.3 }}>{block.title}</Typography>
            <Typography sx={{ mt: .18, color: "text.secondary", fontWeight: 750, fontSize: 10.8 }}>{minuteLabel(block.start)} · {durationLabel(block.minutes)} reales</Typography>
            {block.note && block.note !== block.title ? <Typography sx={{ mt: .2, color: "text.secondary", fontWeight: 700, fontSize: 11.2 }}>{block.note}</Typography> : null}
          </Box>
          <Chip size="small" label="HECHO" sx={{ height: 20, fontWeight: 950, fontSize: 9.5 }} />
        </Stack>
      </Box>
    );
  }

  if (block.type === "note" || block.type === "moment") {
    const isMoment = block.type === "moment";
    return (
      <Box sx={{ px: 1, py: .8, borderRadius: 1.7, border: "1px dashed", borderColor: "divider", bgcolor: "background.paper" }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.65} alignItems={{ sm: "center" }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={isMoment ? "MOMENTO" : "NOTA"} variant="outlined" sx={{ height: 19, fontWeight: 950, fontSize: 9.2 }} />
              <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: 10.8 }}>{minuteLabel(block.start)}</Typography>
            </Stack>
            <Typography sx={{ mt: .3, fontWeight: 850, fontSize: 12.3, lineHeight: 1.35, whiteSpace: "pre-wrap" }}>{block.title}</Typography>
          </Box>
          {!isMoment && block.status === "inbox" ? (
            <Button size="small" onClick={() => onProcessNote?.(block.source)} sx={{ ...compactActionSx, minWidth: 0, flexShrink: 0 }}>Convertir si hace falta</Button>
          ) : !isMoment ? <Chip size="small" label="Procesada" sx={{ height: 20, fontWeight: 850, fontSize: 9.5 }} /> : null}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, py: .8, borderRadius: 1.7, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.55} alignItems={{ sm: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ fontWeight: 950, fontSize: 12.5, lineHeight: 1.3 }}>{block.title}</Typography>
            <Chip size="small" variant="outlined" label={block.type === "routine" ? "Rutina" : "Plan"} sx={{ height: 19, fontWeight: 850, fontSize: 9.5 }} />
            {block.completed ? <Chip size="small" label="Hecho" sx={{ height: 19, fontWeight: 850, fontSize: 9.5 }} /> : null}
          </Stack>
          <Typography sx={{ mt: .18, color: "text.secondary", fontWeight: 720, fontSize: 10.9 }}>
            {minuteLabel(block.start)}–{minuteLabel(block.end)}{block.actualMinutes ? ` · ${durationLabel(block.actualMinutes)} reales` : ""}
          </Typography>
        </Box>
        {!block.completed && block.type === "commitment" ? (
          <Button size="small" startIcon={<TimerRoundedIcon />} onClick={() => onLogTime?.(block.source)} sx={{ ...compactActionSx, minWidth: 0, flexShrink: 0 }}>Registrar tiempo</Button>
        ) : null}
        {!block.completed && block.type === "routine" ? (
          <Button size="small" startIcon={<CheckRoundedIcon />} onClick={() => onRoutineToggle?.(block.source)} sx={{ ...compactActionSx, minWidth: 0, flexShrink: 0 }}>Hecho</Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export function getDayOccupiedIntervals(calendar) {
  return plannedBlocks(calendar).map((block) => ({ start: block.start, end: block.end }));
}

export default function OwnerHourBoard({
  calendar,
  notes = [],
  onScheduleAt,
  onLogTime,
  onLogAt,
  onNoteAt,
  onProcessNote,
  onRoutineToggle,
}) {
  const selectedDate = calendar?.selectedDate || ymd();
  const [zoomMinutes, setZoomMinutes] = React.useState(60);
  const [now, setNow] = React.useState(() => new Date());
  const currentSlotRef = React.useRef(null);
  const today = selectedDate === ymd(now);
  const nowParts = argentinaParts(now);
  const nowMinutes = nowParts?.minutes ?? 0;

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  const planned = React.useMemo(() => plannedBlocks(calendar, now), [calendar, now]);
  const actual = React.useMemo(() => actualBlocks(calendar), [calendar]);
  const noteBlocks = React.useMemo(() => dayNotes(notes, selectedDate), [notes, selectedDate]);
  const allEvents = React.useMemo(() => [...actual, ...planned, ...noteBlocks].sort((a, b) => a.start - b.start || a.type.localeCompare(b.type)), [actual, planned, noteBlocks]);
  const actualMinutes = actual.reduce((sum, block) => sum + Number(block.minutes || 0), 0);
  const slots = [];
  for (let minute = 0; minute < 1440; minute += zoomMinutes) slots.push(minute);

  const zoomIndex = ZOOM_STEPS.indexOf(zoomMinutes);
  const zoomIn = () => setZoomMinutes(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, zoomIndex + 1)]);
  const zoomOut = () => setZoomMinutes(ZOOM_STEPS[Math.max(0, zoomIndex - 1)]);
  const goNow = () => currentSlotRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });

  return (
    <Box data-owner-jarvis-section="today-timeline" sx={{ ...surfaceSx, p: { xs: 1.1, sm: 1.5 }, overflow: "hidden" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "center" }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <AccessTimeRoundedIcon fontSize="small" />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 15, sm: 18 } }}>{today ? `Hoy · ${nowParts?.clock || ""}` : selectedDate}</Typography>
          </Stack>
          <Typography sx={{ mt: .2, color: "text.secondary", fontWeight: 720, fontSize: 12, display: { xs: "none", sm: "block" } }}>
            El día se llena con bloques reales mientras Jarvis registra lo que hacés.
          </Typography>
        </Box>
        <Stack direction="row" spacing={.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<EditNoteRoundedIcon />} onClick={() => onNoteAt?.(localDateTimeInput(selectedDate, today ? nowMinutes : 9 * 60))} sx={{ ...compactActionSx, minWidth: 0, display: { xs: "none", sm: "inline-flex" } }}>Anotar</Button>
          <Tooltip title="Ir a la hora actual"><span><IconButton size="small" onClick={goNow} disabled={!today} sx={{ border: "1px solid", borderColor: "divider" }}><CenterFocusStrongRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          <Stack direction="row" alignItems="center" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.8, px: .2 }}>
            <Tooltip title="Alejar"><span><IconButton size="small" onClick={zoomOut} disabled={zoomIndex <= 0}><RemoveRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
            <Stack direction="row" spacing={.35} alignItems="center" sx={{ px: .3, minWidth: 58, justifyContent: "center" }}><ZoomInRoundedIcon sx={{ fontSize: 15 }} /><Typography sx={{ fontSize: 10.5, fontWeight: 950 }}>{zoomMinutes === 60 ? "1 h" : `${zoomMinutes} min`}</Typography></Stack>
            <Tooltip title="Acercar"><span><IconButton size="small" onClick={zoomIn} disabled={zoomIndex >= ZOOM_STEPS.length - 1}><AddRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap sx={{ mt: .8 }}>
        <Chip size="small" label={`${durationLabel(actualMinutes)} reales`} sx={{ fontWeight: 900 }} />
        <Chip size="small" label={`${noteBlocks.length} notas`} variant="outlined" sx={{ fontWeight: 900, display: { xs: "none", sm: "inline-flex" } }} />
        {today ? <Chip size="small" color="primary" label={`${nowParts?.clock || ""} · AR`} sx={{ fontWeight: 950 }} /> : <Chip size="small" label={selectedDate} sx={{ fontWeight: 900 }} />}
      </Stack>

      <Divider sx={{ mt: 1, mb: .2 }} />

      <Box sx={{ maxHeight: { xs: "68vh", md: "72vh" }, overflowY: "auto", pr: { xs: 0, sm: .45 }, scrollBehavior: "smooth" }}>
        {slots.map((slotStart) => {
          const slotEnd = slotStart + zoomMinutes;
          const slotEvents = allEvents.filter((block) => bucketOf(block.start, zoomMinutes) === slotStart);
          const coveringEvents = allEvents.filter((block) => block.type !== "note" && block.type !== "moment" && block.start < slotStart && block.end > slotStart);
          const isCurrent = today && nowMinutes >= slotStart && nowMinutes < slotEnd;
          const isPast = today && slotEnd <= nowMinutes;
          const markerTop = isCurrent ? `${((nowMinutes - slotStart) / zoomMinutes) * 100}%` : null;
          return (
            <Box
              key={slotStart}
              ref={isCurrent ? currentSlotRef : undefined}
              sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: { xs: "48px minmax(0,1fr)", sm: "62px minmax(0,1fr)" },
                gap: { xs: .65, sm: 1 },
                minHeight: zoomMinutes === 60 ? 58 : zoomMinutes === 30 ? 48 : 42,
                py: .45,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: isCurrent ? "rgba(243,90,58,.035)" : "transparent",
              }}
            >
              {isCurrent ? <Box sx={{ position: "absolute", left: { xs: 44, sm: 58 }, right: 0, top: markerTop, height: 2, bgcolor: "primary.main", zIndex: 3, pointerEvents: "none", "&::before": { content: '""', position: "absolute", left: -4, top: -3, width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" } }} /> : null}
              <Box sx={{ pt: .2 }}>
                <Typography sx={{ fontWeight: 950, fontSize: 10.8, color: isCurrent ? "primary.main" : "text.secondary" }}>{minuteLabel(slotStart)}</Typography>
                {isCurrent ? <Typography sx={{ mt: .1, color: "primary.main", fontWeight: 1000, fontSize: 8.8 }}>AHORA</Typography> : null}
              </Box>
              <Stack spacing={.42} sx={{ minWidth: 0, justifyContent: "center" }}>
                {slotEvents.map((block) => <EventCard key={block.id} block={block} onLogTime={onLogTime} onRoutineToggle={onRoutineToggle} onProcessNote={onProcessNote} />)}
                {!slotEvents.length && coveringEvents.map((block) => <ContinuationCard key={`continuation:${block.id}:${slotStart}`} block={block} />)}
                {!slotEvents.length && !coveringEvents.length ? (
                  <Stack direction="row" justifyContent="space-between" spacing={.5} alignItems="center" sx={{ minHeight: 30, opacity: isPast ? .72 : 1 }}>
                    <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 10.8 }}>{isPast ? "Sin registro" : "Libre"}</Typography>
                    <Stack direction="row" spacing={.25}>
                      {isPast || isCurrent ? <Button size="small" onClick={() => onLogAt?.(localDateTimeInput(selectedDate, isCurrent ? nowMinutes : slotStart))} sx={{ ...compactActionSx, minWidth: 0, px: .65, fontSize: 10 }}>Registrar</Button> : null}
                      <Button size="small" onClick={() => onNoteAt?.(localDateTimeInput(selectedDate, isCurrent ? nowMinutes : slotStart))} sx={{ ...compactActionSx, minWidth: 0, px: .65, fontSize: 10 }}>Nota</Button>
                      {!isPast && !isCurrent ? <Button size="small" onClick={() => onScheduleAt?.(localDateTimeInput(selectedDate, slotStart))} sx={{ ...compactActionSx, minWidth: 0, px: .65, fontSize: 10 }}>Planificar</Button> : null}
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
